import React, { useEffect, useRef, useState, useCallback } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import api from '../services/api';
import { Loader2, SkipForward, Volume2, VolumeX } from 'lucide-react';

/**
 * VideoPlayerWithAds - Mux player with Google IMA SDK ad integration
 * 
 * Features:
 * - Pre-roll ads before video starts
 * - Mid-roll ads at configurable intervals
 * - Skippable ads for premium users, unskippable for free users
 * - Ad tracking and analytics
 */
const VideoPlayerWithAds = ({
  playbackId,
  contentId,
  poster,
  autoPlay = false,
  isPremiumUser = false,
  onVideoStart,
  onVideoEnd,
  className = '',
}) => {
  const playerRef = useRef(null);
  const adContainerRef = useRef(null);
  const adsManagerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  
  const [adPlaying, setAdPlaying] = useState(false);
  const [adSkippable, setAdSkippable] = useState(false);
  const [skipCountdown, setSkipCountdown] = useState(0);
  const [adMuted, setAdMuted] = useState(false);
  const [adLoading, setAdLoading] = useState(true);
  const [showPreroll, setShowPreroll] = useState(true);
  const [videoStarted, setVideoStarted] = useState(false);
  const [midrollTimes, setMidrollTimes] = useState([]);
  const [playedMidrolls, setPlayedMidrolls] = useState(new Set());

  // AdSense Publisher ID
  const ADSENSE_PUBLISHER_ID = 'ca-pub-8055818286907680';

  // Ad tag URL - Using Google AdSense for Video
  const getAdTagUrl = useCallback(() => {
    // Google AdSense for Video (AFV) VAST tag
    const baseUrl = 'https://googleads.g.doubleclick.net/pagead/ads';
    const params = new URLSearchParams({
      client: ADSENSE_PUBLISHER_ID,
      ad_type: 'video',
      description_url: encodeURIComponent(window.location.href),
      max_ad_duration: '30000', // 30 seconds max
      videoad_start_delay: '0', // Pre-roll
      hl: 'en',
      vpos: 'preroll',
      output: 'vast',
      unviewed_position_start: '1',
      url: encodeURIComponent(window.location.href),
      correlator: Date.now(),
    });
    return `${baseUrl}?${params.toString()}`;
  }, []);

  // Initialize IMA SDK
  useEffect(() => {
    // Load Google IMA SDK
    const script = document.createElement('script');
    script.src = '//imasdk.googleapis.com/js/sdkloader/ima3.js';
    script.async = true;
    script.onload = initializeAds;
    script.onerror = () => {
      console.error('Failed to load IMA SDK');
      setAdLoading(false);
      setShowPreroll(false);
    };
    document.head.appendChild(script);

    return () => {
      if (adsManagerRef.current) {
        adsManagerRef.current.destroy();
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const initializeAds = useCallback(() => {
    if (!window.google || !window.google.ima) {
      setAdLoading(false);
      setShowPreroll(false);
      return;
    }

    try {
      const adDisplayContainer = new window.google.ima.AdDisplayContainer(
        adContainerRef.current,
        null // Content video element - we'll handle playback separately
      );
      
      adsLoaderRef.current = new window.google.ima.AdsLoader(adDisplayContainer);
      
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded
      );
      
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        onAdError
      );

      // Request pre-roll ad
      requestAd('preroll');
      
      // Set up mid-roll times (every 5 minutes)
      setMidrollTimes([300, 600, 900, 1200, 1500]); // 5, 10, 15, 20, 25 minutes
      
    } catch (e) {
      console.error('Error initializing IMA:', e);
      setAdLoading(false);
      setShowPreroll(false);
    }
  }, []);

  const requestAd = (adType) => {
    if (!adsLoaderRef.current) return;
    
    const adsRequest = new window.google.ima.AdsRequest();
    adsRequest.adTagUrl = getAdTagUrl();
    adsRequest.linearAdSlotWidth = 640;
    adsRequest.linearAdSlotHeight = 360;
    adsRequest.nonLinearAdSlotWidth = 640;
    adsRequest.nonLinearAdSlotHeight = 150;

    adsLoaderRef.current.requestAds(adsRequest);
  };

  const onAdsManagerLoaded = (adsManagerLoadedEvent) => {
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    
    adsManagerRef.current = adsManagerLoadedEvent.getAdsManager(
      document.createElement('video'), // Dummy video element
      adsRenderingSettings
    );

    // Add event listeners
    adsManagerRef.current.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      onAdError
    );
    adsManagerRef.current.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      onContentPauseRequested
    );
    adsManagerRef.current.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      onContentResumeRequested
    );
    adsManagerRef.current.addEventListener(
      window.google.ima.AdEvent.Type.STARTED,
      onAdStarted
    );
    adsManagerRef.current.addEventListener(
      window.google.ima.AdEvent.Type.COMPLETE,
      onAdComplete
    );
    adsManagerRef.current.addEventListener(
      window.google.ima.AdEvent.Type.SKIPPED,
      onAdSkipped
    );

    setAdLoading(false);
    
    // Auto-play pre-roll for free users
    if (showPreroll && autoPlay) {
      playAd();
    }
  };

  const onAdError = (adErrorEvent) => {
    console.error('Ad error:', adErrorEvent.getError().getMessage());
    setAdLoading(false);
    setAdPlaying(false);
    setShowPreroll(false);
    
    // Track ad error
    trackAdEvent('error', 'preroll');
    
    // Resume content
    resumeContent();
  };

  const onContentPauseRequested = () => {
    setAdPlaying(true);
  };

  const onContentResumeRequested = () => {
    setAdPlaying(false);
    setShowPreroll(false);
    resumeContent();
  };

  const onAdStarted = (adEvent) => {
    const ad = adEvent.getAd();
    
    // Track ad impression
    trackAdEvent('impression', showPreroll ? 'preroll' : 'midroll');
    
    // For premium users, ads become skippable after 5 seconds
    // For free users, no skip option
    if (isPremiumUser) {
      setSkipCountdown(5);
      const countdownInterval = setInterval(() => {
        setSkipCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setAdSkippable(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const onAdComplete = () => {
    setAdPlaying(false);
    setAdSkippable(false);
    setSkipCountdown(0);
    
    // Track ad completion
    trackAdEvent('complete', showPreroll ? 'preroll' : 'midroll');
  };

  const onAdSkipped = () => {
    setAdPlaying(false);
    setAdSkippable(false);
    setSkipCountdown(0);
    
    // Track ad skip
    trackAdEvent('skip', showPreroll ? 'preroll' : 'midroll');
  };

  const playAd = () => {
    if (!adsManagerRef.current) {
      setShowPreroll(false);
      return;
    }

    try {
      adsManagerRef.current.init(640, 360, window.google.ima.ViewMode.NORMAL);
      adsManagerRef.current.start();
    } catch (e) {
      console.error('Error starting ad:', e);
      setShowPreroll(false);
      resumeContent();
    }
  };

  const skipAd = () => {
    if (adsManagerRef.current && adSkippable) {
      adsManagerRef.current.skip();
    }
  };

  const toggleAdMute = () => {
    if (adsManagerRef.current) {
      if (adMuted) {
        adsManagerRef.current.setVolume(1);
      } else {
        adsManagerRef.current.setVolume(0);
      }
      setAdMuted(!adMuted);
    }
  };

  const resumeContent = () => {
    if (!videoStarted) {
      setVideoStarted(true);
      onVideoStart?.();
    }
  };

  const handlePlayClick = () => {
    if (showPreroll && !videoStarted) {
      playAd();
    } else {
      resumeContent();
    }
  };

  const handleTimeUpdate = (currentTime) => {
    // Check for mid-roll ads
    for (const time of midrollTimes) {
      if (currentTime >= time && !playedMidrolls.has(time)) {
        setPlayedMidrolls(prev => new Set([...prev, time]));
        // Request and play mid-roll ad
        if (!isPremiumUser) {
          // Only show mid-rolls to free users, premium users skip them
          requestAd('midroll');
        }
        break;
      }
    }
  };

  const trackAdEvent = async (eventType, adType) => {
    try {
      await api.post('/analytics/track-ad-event', null, {
        params: { event_type: eventType, ad_type: adType, content_id: contentId }
      });
    } catch (e) {
      console.error('Error tracking ad event:', e);
    }
  };

  // Track video view
  const trackView = async (duration, completed) => {
    try {
      await api.post('/analytics/track-view', {
        content_id: contentId,
        duration_watched: Math.floor(duration),
        completed
      });
    } catch (e) {
      console.error('Error tracking view:', e);
    }
  };

  if (!playbackId) {
    return (
      <div className={`relative bg-black flex items-center justify-center ${className}`}>
        <p className="text-white/60">No video available</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Main Video Player */}
      <div 
        ref={playerRef}
        className={`w-full h-full ${adPlaying ? 'invisible' : 'visible'}`}
      >
        <MuxPlayer
          playbackId={playbackId}
          poster={poster}
          autoPlay={!showPreroll && autoPlay}
          streamType="on-demand"
          style={{ width: '100%', height: '100%' }}
          primaryColor="#00F5D4"
          secondaryColor="#000000"
          accentColor="#00F5D4"
          onTimeUpdate={(e) => handleTimeUpdate(e.target.currentTime)}
          onEnded={(e) => {
            trackView(e.target.duration, true);
            onVideoEnd?.();
          }}
        />
      </div>

      {/* Ad Container Overlay */}
      <div
        ref={adContainerRef}
        className={`absolute inset-0 bg-black ${adPlaying ? 'visible' : 'invisible'}`}
        style={{ zIndex: 20 }}
      />

      {/* Ad Controls Overlay */}
      {adPlaying && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded">
                AD
              </span>
              {!isPremiumUser && (
                <span className="text-white/70 text-sm">
                  Upgrade to Premium to skip ads
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Mute Button */}
              <button
                onClick={toggleAdMute}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                {adMuted ? (
                  <VolumeX className="h-4 w-4 text-white" />
                ) : (
                  <Volume2 className="h-4 w-4 text-white" />
                )}
              </button>
              
              {/* Skip Button (Premium only) */}
              {isPremiumUser && (
                <button
                  onClick={skipAd}
                  disabled={!adSkippable}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    adSkippable
                      ? 'bg-white text-black hover:bg-white/90'
                      : 'bg-white/20 text-white/60 cursor-not-allowed'
                  }`}
                >
                  {adSkippable ? (
                    <>
                      <SkipForward className="h-4 w-4" />
                      <span>Skip Ad</span>
                    </>
                  ) : (
                    <span>Skip in {skipCountdown}s</span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pre-roll Play Overlay */}
      {showPreroll && !adPlaying && !videoStarted && (
        <div 
          className="absolute inset-0 bg-black/60 flex items-center justify-center z-10 cursor-pointer"
          onClick={handlePlayClick}
        >
          {adLoading ? (
            <div className="flex flex-col items-center space-y-3">
              <Loader2 className="h-12 w-12 text-white animate-spin" />
              <span className="text-white/70">Loading ad...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <div className="w-20 h-20 rounded-full bg-electric flex items-center justify-center hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <span className="text-white/70 text-sm">
                {isPremiumUser ? 'Ad will play (skippable)' : 'Ad will play before video'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlayerWithAds;
